import { ResultOf } from "gql.tada";
import random from "lodash/random";
import {
  ComponentProps,
  CSSProperties,
  RefObject,
  useMemo,
  useRef,
  useState,
} from "react";
import { SlidingThumbnailHeroById } from "@queries/entries/SlidingThumbnailHeroById";
import { motion } from "motion/react";
import { ThemedRadialGradient } from "./ThemedRadialGradient";
import cx from "classnames";
import { MediaItem } from "@components/shared/MediaItem";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { animationCurves } from "@utils/animationCurves";

const RANDOM_RANGE = 100;

export const InfiniteAnimationFrame = ({
  entry,
}: {
  entry: NonNullable<
    ResultOf<typeof SlidingThumbnailHeroById>["slidingThumbnailHero"]
  >;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pause, setPause] = useState(false);

  const itemCount = entry?.themedMediaItemsCollection?.items?.length ?? 0;

  const randomTranslations = useMemo(
    () =>
      Array.from({ length: itemCount }, () => {
        return {
          x: random(-RANDOM_RANGE, RANDOM_RANGE),
          y: random(-RANDOM_RANGE, RANDOM_RANGE),
        };
      }),
    [itemCount]
  );

  const itemProps = {
    onMouseEnter: () => setPause(true),
    onMouseLeave: () => setPause(false),
  };

  return (
    <div
      ref={containerRef}
      className="h-screen w-[400vw] dsk:w-[200vw] flex animate-slidingThumbnailHeroTranslateLoop"
      style={{ animationPlayState: pause ? "paused" : "running" }}
    >
      <Frame
        containerRef={containerRef}
        delay={1}
        itemProps={itemProps}
        entry={entry}
        randomTranslations={randomTranslations}
      />
      <Frame
        containerRef={containerRef}
        delay={2}
        itemProps={itemProps}
        entry={entry}
        randomTranslations={randomTranslations}
      />
    </div>
  );
};

const Frame = ({
  entry,
  randomTranslations,
  itemProps = {},
  delay: initialDelay,
  containerRef,
}: {
  entry: ResultOf<typeof SlidingThumbnailHeroById>["slidingThumbnailHero"];
  randomTranslations: Array<{ x: number; y: number }>;
  itemProps?: ComponentProps<typeof motion.div>;
  delay: number;
  containerRef: RefObject<Element>;
}) => {
  const isMobile = !useIsDesktop();
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="relative flex flex-col flex-wrap h-full w-[200vw] dsk:w-screen">
      {entry?.themedMediaItemsCollection?.items?.map((item, index) => {
        const inset = ((): CSSProperties => {
          switch (index) {
            case 0:
              return {
                top: "25%",
                left: "25%",
              };
            case 1:
              return {
                top: "25%",
                left: "75%",
              };
            case 2:
              return {
                top: "75%",
                left: "25%",
              };
            case 3:
              return {
                top: "75%",
                left: "75%",
              };
            case 4:
              return {
                top: "50%",
                left: "50%",
              };
            default:
              return {};
          }
        })();
        const mediaItem =
          (isMobile ? item?.mobileMedia : item?.media) ??
          item?.media ??
          item?.mobileMedia;

        const mobileMedia = item?.mobileMedia;

        return (
          <div
            className="absolute aspect-video w-[224px] dsk:w-[450px] flex justify-center items-center"
            key={index}
            style={{
              transform: `translate(calc(-50% + ${randomTranslations[index].x}px), calc(-50% + ${randomTranslations[index].y}px))`,
              ...inset,
            }}
          >
            <motion.div
              {...itemProps}
              className={cx(
                "w-[164px] dsk:w-[332px] aspect-video transition-[width] peer group",
                itemProps.className
              )}
              onMouseEnter={(e) => {
                if (isLoaded) {
                  itemProps.onMouseEnter?.(e);
                  e.currentTarget.style.width = "100%";
                  e.currentTarget.ariaSelected = "true";
                }
              }}
              onMouseLeave={(e) => {
                itemProps.onMouseLeave?.(e);
                e.currentTarget.style.width = "";
                e.currentTarget.ariaSelected = null;
              }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                delay: 0.15 * index + initialDelay,
                ...animationCurves.long,
              }}
              custom={index}
            >
              {mediaItem && (
                <MediaItem
                  alt=""
                  poster={mobileMedia?.url ?? ""}
                  item={mediaItem}
                  className="rounded-xl brightness-80 transition-all group-aria-selected:brightness-100"
                  autoPlay
                  muted
                  loop
                  playsInline
                  // These are purposefully just a little bigger than the width when they are not hovered.
                  width={isMobile ? 250 : 500}
                  setIsLoaded={setIsLoaded}
                  onScreenDeterminationElementRef={containerRef}
                />
              )}
            </motion.div>
            <div className="absolute transition-opacity opacity-0 peer-aria-selected:opacity-100 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-50 rounded-full pointer-events-none">
              <ThemedRadialGradient palette={item?.theme?.palette ?? ""} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
